import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
  ExclamationCircleFilled,
  CreditCardOutlined,
  WalletOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Popover } from 'antd';

import generatePath from '~settings/routing';
import { bookingJourneyType } from '~constants';

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      ...theme.flex.crossCenter,
      backgroundColor: theme.colors.orange1,
      borderRadius: 2,
      padding: '6px 12px',
      ...theme.typography.body4,
      color: theme.colors.orange6,
    },

    popContent: {
      '& > .ant-popover-content': {
        borderRadius: 2,
        '& > .ant-popover-arrow': {
          right: 22,
          borderRightColor: 'rgba(0, 0, 0, 0.75)',
          borderBottomColor: 'rgba(0, 0, 0, 0.75)',
        },
        '& > .ant-popover-inner': {
          padding: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',

          '& .ant-popover-inner-content': {
            padding: 0,
            color: theme.colors.gray1,
          },
        },
      },
    },

    tipIcon: {
      marginRight: 8,
      color: theme.colors.orange11,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    tipButton: {
      ...theme.typography.body6,
      ...theme.flex.crossCenter,
      width: 'fit-content',
      border: `1px solid ${theme.colors.orange2}`,
      borderRadius: 2,
      padding: '0 8px',
      marginLeft: 6,
      cursor: 'pointer',

      '&:hover': {
        borderColor: theme.colors.gray5,
        backgroundColor: theme.colors.gray1,
        color: theme.colors.bodyColor,

        '& > span:first-child': {
          color: theme.colors.disableColor,
        },
      },
    },

    tipButtonIcon: {
      display: 'flex',
      alignItems: 'center',
      marginRight: 6,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    tipButtonRightIcon: {
      marginLeft: 1,

      '& > svg': {
        width: 8,
        height: 8,
      },
    },

    tipsLeft: {
      marginLeft: 6,
    },
  }),
  { name: 'pendingAlertTips' },
);

const PendingAlertTips = ({ property, history, setCurrentSection, from }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const {
    publishCondition: { commissionTier, contractStage, paymentPlan, payout, offlinePaymentMethod },
  } = property;
  const paymentOnline = !property?.paymentPlan || property?.paymentPlan?.paymentOnline;
  const paymentUrl = generatePath('paymentList');
  const payoutUrl = generatePath('withdrawal.list');
  const paymentMethodUrl = generatePath(
    'property.edit',
    { propertySlug: property?.slug },
    { section: 'PAYMENT_METHOD' },
  );
  const isManual = [bookingJourneyType.MANUAL, bookingJourneyType.FULL_AUTOMATIC].includes(
    property?.bookingJourney,
  );

  const handleGotoSet = key => {
    const targetUrl = paymentOnline ? payoutUrl : paymentMethodUrl;
    if (setCurrentSection && !paymentOnline && key !== 'payment') {
      setCurrentSection('PAYMENT_METHOD');
    }
    history.push(key === 'payment' ? paymentUrl : targetUrl);
  };

  const labelContent = key => (
    <Popover
      overlayClassName={styles.popContent}
      content={t(`common.pending_alert_tips.content.${key}`)}
    >
      <span className={styles.tipButton} onClick={() => handleGotoSet(key)}>
        {key === 'payment' ? (
          <WalletOutlined className={styles.tipButtonIcon} />
        ) : (
          <CreditCardOutlined className={styles.tipButtonIcon} />
        )}
        <div>{t(`pending_alert_tips.${key}`)}</div>
        <RightOutlined className={styles.tipButtonRightIcon} />
      </span>
    </Popover>
  );

  const showPaymentMethod = useMemo(() => {
    if (paymentPlan && !paymentOnline) {
      return true;
    }
    return false;
  }, [paymentPlan, property]);

  const isSetted = useMemo(
    () => (showPaymentMethod ? offlinePaymentMethod : payout),
    [payout, offlinePaymentMethod, showPaymentMethod],
  );

  if (commissionTier && contractStage && paymentPlan && isSetted) {
    return null;
  }

  return (
    <div className={styles.wrapper} style={{ marginBottom: from === 'propertyHomepage' && 24 }}>
      <ExclamationCircleFilled className={styles.tipIcon} />
      <If condition={!isManual && (!paymentPlan || !payout || !offlinePaymentMethod)}>
        <span>{t('pending_alert_tips.pre_tips')}</span>
        <Choose>
          <When condition={!paymentPlan && paymentOnline && !payout}>
            {labelContent('payment')}&nbsp;and{labelContent('payout')}&nbsp;.
          </When>
          <When condition={!paymentPlan && !paymentOnline && !offlinePaymentMethod}>
            {labelContent('payment')}&nbsp;and{labelContent('payment_method')}&nbsp;.
          </When>
          <When condition={!paymentPlan}>{labelContent('payment')}. </When>
          <When condition={paymentOnline && !payout}>{labelContent('payout')}. </When>
          <When condition={!paymentOnline && !offlinePaymentMethod}>
            {labelContent('payment_method')}.
          </When>
        </Choose>
      </If>
      <Choose>
        <When condition={!commissionTier && !contractStage}>
          <span className={styles.tipsLeft}>
            {t('pending_alert_tips.commission_contract_tips')}
          </span>
        </When>
        <When condition={!commissionTier}>
          <span className={styles.tipsLeft}>{t('pending_alert_tips.commission_tips')}</span>
        </When>
        <When condition={!contractStage}>
          <span className={styles.tipsLeft}>{t('pending_alert_tips.contract_tips')}</span>
        </When>
      </Choose>
    </div>
  );
};

PendingAlertTips.propTypes = {
  property: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default PendingAlertTips;
